////////////////////////////////////////////////////////////////////////////////
//                                                                             /
//                                                                             /
//  (C) Copyright 2024 Autodesk, Inc. All rights reserved.                     /
//                                                                             /
//                       ****  CONFIDENTIAL MATERIAL  ****                     /
//                                                                             /
//  The information contained herein is confidential, proprietary to           /
//  Autodesk, Inc., and considered a trade secret.  Use of this information    /
//  by anyone other than authorized employees of Autodesk, Inc. is granted     /
//  only under a written nondisclosure agreement, expressly prescribing        /
//  the scope and manner of such use.                                          /
//                                                                             /
////////////////////////////////////////////////////////////////////////////////

import React, {useCallback, useEffect, useReducer} from 'react';
import {reducer} from "./reducers/ProjectWiseSettingsReducer";
import {ProjectWiseSettingsState} from "./states/ProjectWiseSettingsState";
import {
  BlueButton,
  ColumnLeft,
  ColumnRight,
  FlexColumn,
  FlexFill,
  FlexRow,
  FlexRowCentered
} from "../CommonStyledComponents";
import ProjectWiseCredentialsList from "./ProjectWiseCredentialsList";
import ProjectWiseConfigurationsList from "./ProjectWiseConfigurationsList";
import {ArrowRotateTwoIcon, PlusIcon} from "@adsk/alloy-react-icon";
import Theme from "@adsk/alloy-react-theme";
import {IconButton} from "@adsk/alloy-react-button";
import Tooltip from "@adsk/alloy-react-tooltip";
import {ProjectWiseService} from "../services/ProjectWiseService";
import {PageSizeService} from "../services/PageSizeService";
import {CancellationToken} from "../dataModel/CancellationToken";
import {ProjectWiseActionTypes, ProjectWiseSettingsActions} from "../Enums";
import {ProjectWiseConfigurationTranslator} from "../dataModel/translators/ProjectWiseConfigurationTranslator";
import {ProjectWiseConfiguration, ProjectWiseCredential, UpdateProjectWiseConfigurationDto} from "../clients/Classes";
import {ProjectWiseConfigurationUI} from "../dataModel/ProjectWiseConfigurationUI";
import {SetConfigurationCredentials} from "../Utility";
import Modal from "@adsk/alloy-react-modal";
import TextInput from "@adsk/alloy-react-text-input";
import Selector from "./Selector";
import MaskedText from "./MaskedText";

const projectWiseService = new ProjectWiseService();

const pageSizeCredentials = PageSizeService.GetPageSize('projectWiseCredentials');
let paginationTokenCredentials: string | undefined = undefined;
const cancelTokenCredentials = new CancellationToken();

const pageSizeConfigurations = PageSizeService.GetPageSize('projectWiseConfigurations');
let paginationTokenConfigurations: string | undefined = undefined;
const cancelTokenConfigurations = new CancellationToken();

let originalPassword: string | undefined;

const ProjectWiseSettings = ({onError}: { onError?: (error: any, operation: string) => void }) => {
  const [state, dispatch] = useReducer(reducer, new ProjectWiseSettingsState());

  const refreshCredentials = useCallback((isFirstLoad: boolean, loadAll: boolean) => {
    dispatch({
      type: ProjectWiseSettingsActions.multipleActions,
      payload: {
        LoadingCredentials: true,
        CanCancelCredentials: loadAll
      }
    });

    if (isFirstLoad) {
      paginationTokenCredentials = undefined;
    }

    cancelTokenCredentials.Cancel = false;

    const promise = loadAll
      ? projectWiseService.GetRemainingCredentials(paginationTokenCredentials, pageSizeCredentials, cancelTokenCredentials)
      : projectWiseService.GetCredentials(paginationTokenCredentials, pageSizeCredentials);

    return promise
      .then(paginationData => {
        paginationTokenCredentials = paginationData.paginationData?.paginationToken;

        if (!isFirstLoad) {
          state.Credentials.forEach(c => paginationData.items!.push(c));
        }

        dispatch({
          type: ProjectWiseSettingsActions.multipleActions,
          payload: {
            LoadingCredentials: false,
            Credentials: paginationData.items,
            HasMoreCredentials: !paginationData.isDone
          }
        });

        return paginationData.items!;
      })
      .catch(error => {
        dispatch({type: ProjectWiseSettingsActions.loadingCredentials, payload: false});
        handleError(error, 'Get ProjectWise Credentials');
        return [];
      });
  }, []);

  const refreshConfigs = useCallback((isFirstLoad: boolean, loadAll: boolean) => {
    dispatch({
      type: ProjectWiseSettingsActions.multipleActions,
      payload: {
        LoadingConfigurations: true,
        CanCancelConfigurations: loadAll
      }
    });

    if (isFirstLoad) {
      paginationTokenConfigurations = undefined;
    }

    cancelTokenConfigurations.Cancel = false;

    const promise = loadAll
      ? projectWiseService.GetRemainingConfigurations(paginationTokenConfigurations, pageSizeConfigurations, cancelTokenConfigurations)
      : projectWiseService.GetConfigurations(paginationTokenConfigurations, pageSizeConfigurations);

    return promise
      .then(paginationData => {
        paginationTokenConfigurations = paginationData.paginationData?.paginationToken;

        const configs = paginationData.items!
          .map(c => ProjectWiseConfigurationTranslator.TranslateConfiguration(c));

        if (!isFirstLoad) {
          state.Configurations.forEach(c => configs.push(c));
        }

        SetConfigurationCredentials(configs, state.Credentials);

        dispatch({
          type: ProjectWiseSettingsActions.multipleActions,
          payload: {
            LoadingConfigurations: false,
            Configurations: configs,
            HasMoreConfigurations: !paginationData.isDone
          }
        });

        return configs;
      })
      .catch(error => {
        dispatch({type: ProjectWiseSettingsActions.loadingConfigurations, payload: false});
        handleError(error, 'Get ProjectWise Configurations');
        return [];
      });
  }, [state.Credentials]);

  useEffect(() => {
    const promises = [
      refreshCredentials(true, false),
      refreshConfigs(true, false)
    ];

    Promise.all(promises)
      .then(results => {
        const credentials: ProjectWiseCredential[] = [];
        const configs: ProjectWiseConfigurationUI[] = [];

        results.forEach(resultSet => {
          resultSet.forEach(result => {
            if (result instanceof ProjectWiseConfigurationUI) {
              configs.push(result);
            } else if (result instanceof ProjectWiseCredential) {
              credentials.push(result);
            }
          });
        })

        const hasUpdate = SetConfigurationCredentials(configs, credentials);

        if (hasUpdate) {
          dispatch({type: ProjectWiseSettingsActions.configurations, payload: configs});
        }
      });
  }, []);

  function startNewCredential(): void {
    dispatch({
      type: ProjectWiseSettingsActions.multipleActions,
      payload: {
        EditingCredential: new ProjectWiseCredential(),
        EditIsNew: true,
        IsEditingCredential: true
      }
    });
  }

  function startNewConfiguration(): void {
    const credentialOptions = state.Credentials.map(c => ({value: c.id, label: c.key}));
    const selected = credentialOptions.length > 0 ? credentialOptions[0] : undefined;

    dispatch({
      type: ProjectWiseSettingsActions.multipleActions,
      payload: {
        EditingConfiguration: new ProjectWiseConfigurationUI(new ProjectWiseConfiguration()),
        EditIsNew: true,
        IsEditingConfiguration: true,
        CredentialSelectOptions: credentialOptions,
        SelectedCredential: selected
      }
    });
  }

  function configurationAction(action: ProjectWiseActionTypes, config: ProjectWiseConfigurationUI): void {
    const credentialOptions = state.Credentials.map(c => ({value: c.id, label: c.key}));
    const selected = credentialOptions.find(c => c.value === config.ApiConfiguration.credentialId);

    switch (action) {
      case "edit":
        dispatch({
          type: ProjectWiseSettingsActions.multipleActions,
          payload: {
            EditingConfiguration: config,
            EditIsNew: false,
            IsEditingConfiguration: true,
            CredentialSelectOptions: credentialOptions,
            SelectedCredential: selected
          }
        });
        break;
      case "delete":
        projectWiseService.DeleteConfiguration(config)
          .then(() => {
            const index = state.Configurations.findIndex(c => c.ApiConfiguration.id === config.ApiConfiguration.id);
            if (index < 0) {
              return;
            }

            state.Configurations.splice(index, 1);
            dispatch({type: ProjectWiseSettingsActions.configurations, payload: [...state.Configurations]});
          })
          .catch(error => handleError(error, 'Delete Credential'));
        break;
      case "duplicate":
        const newConfig = new ProjectWiseConfigurationUI(new ProjectWiseConfiguration(config.ApiConfiguration));
        newConfig.ApiConfiguration.id = '';
        newConfig.CredentialKey = config.CredentialKey;

        dispatch({
          type: ProjectWiseSettingsActions.multipleActions,
          payload: {
            EditingConfiguration: newConfig,
            EditIsNew: true,
            IsEditingConfiguration: true,
            CredentialSelectOptions: credentialOptions,
            SelectedCredential: selected
          }
        });
        break;
    }
  }

  function credentialAction(action: ProjectWiseActionTypes, credential: ProjectWiseCredential): void {
    switch (action) {
      case "edit":
        getPassword(credential.id!)
          .then(pw => {
            dispatch({
              type: ProjectWiseSettingsActions.multipleActions,
              payload: {
                EditingCredential: credential,
                EditIsNew: false,
                IsEditingCredential: true,
                EditPassword: pw
              }
            });
          })
          .catch(error => handleError(error, 'Get Password'));
        break;
      case "delete":
        projectWiseService.DeleteCredential(credential.id!)
          .then(() => {
            const index = state.Credentials.findIndex(c => c.id === credential.id);
            if (index < 0) {
              return;
            }

            state.Credentials.splice(index, 1);
            dispatch({type: ProjectWiseSettingsActions.credentials, payload: [...state.Credentials]});
          })
          .catch(error => handleError(error, 'Delete Credential'));
        break;
      case "duplicate":
        getPassword(credential.id!)
          .then(pw => {
            const newCredential = new ProjectWiseCredential(credential);
            newCredential.id = '';
            newCredential.key += ' - Copy';

            dispatch({
              type: ProjectWiseSettingsActions.multipleActions,
              payload: {
                EditingCredential: newCredential,
                EditIsNew: true,
                IsEditingCredential: true,
                EditPassword: pw
              }
            });
          })
          .catch(error => handleError(error, 'Get Password'));
        break;
    }
  }

  function finishEditCredential(): void {
    dispatch({type: ProjectWiseSettingsActions.isEditingCredential, payload: false});

    if (state.EditIsNew) {
      projectWiseService.CreateCredential(state.EditingCredential!.key!, state.EditingCredential!.username!, state.EditPassword!)
        .then(credential => {
          dispatch({type: ProjectWiseSettingsActions.credentials, payload: [...state.Credentials, credential]});
        })
        .catch(error => handleError(error, 'Create Credential'));
    } else {
      projectWiseService.UpdateCredential(state.EditingCredential!.id!, state.EditingCredential!.key!, state.EditingCredential!.username!, state.EditPassword === originalPassword ? undefined : state.EditPassword)
        .then(credential => {
          const index = state.Credentials.findIndex(c => c.id === credential.id);
          if (index < 0) {
            dispatch({type: ProjectWiseSettingsActions.credentials, payload: [...state.Credentials, credential]});
          } else {
            state.Credentials[index] = credential;
            dispatch({type: ProjectWiseSettingsActions.credentials, payload: [...state.Credentials]});
          }
        })
        .catch(error => handleError(error, 'Update Credential'));
    }
  }

  function finishEditConfiguration(): void {
    dispatch({type: ProjectWiseSettingsActions.isEditingConfiguration, payload: false});
    const dto = new UpdateProjectWiseConfigurationDto({
      repositoryId: state.EditingConfiguration!.ApiConfiguration.repositoryId,
      serverAddress: state.EditingConfiguration!.ApiConfiguration.serverAddress,
      repositoryName: state.EditingConfiguration!.ApiConfiguration.repositoryName,
      credentialId: state.SelectedCredential?.value === state.EditingConfiguration!.ApiConfiguration.id
        ? undefined
        : state.SelectedCredential?.value
    });
    projectWiseService.UpdateConfiguration(state.EditingConfiguration!.ApiConfiguration.id!, dto)
      .then(config => {
        const index = state.Configurations.findIndex(c => c.ApiConfiguration.id === config.ApiConfiguration.id);

        if (index < 0) {
          state.Configurations.push(config);
        } else {
          state.Configurations[index] = config;
        }

        dispatch({type: ProjectWiseSettingsActions.configurations, payload: [...state.Configurations]});
      })
      .catch(error => handleError(error, 'Update Configuration'));
  }

  function getPassword(id: string): Promise<string> {
    if (state.EditPassword != null && state.EditPassword.trim() !== '') {
      return Promise.resolve('');
    }

    return projectWiseService.GetCredentialWithSecret(id)
      .then(credential => {
        originalPassword = credential.password;
        return credential.password!;
      })
      .catch(error => {
        handleError(error, 'Get password');
        return '';
      });
  }

  function handleError(error: any, operation: string): void {
    if (onError) {
      onError(error, operation);
    }
  }

  return (
    <FlexColumn>
      <FlexColumn>
        <h3>Credentials</h3>
        <FlexRowCentered style={{padding: '0.5em 0', flex: 0}}>
          <BlueButton onClick={startNewCredential}>
            <FlexRowCentered>
              <PlusIcon style={{marginRight: '0.5em'}}/>
              <span style={Theme.typography.labelMedium}>New Credential</span>
            </FlexRowCentered>
          </BlueButton>
          <FlexFill/>
          <Tooltip content={'Refresh Credentials'}>
            <IconButton
              onClick={() => refreshCredentials(true, false)}
              renderIcon={() => <ArrowRotateTwoIcon/>}
              style={{marginRight: '1em'}}/>
          </Tooltip>
        </FlexRowCentered>
        <ProjectWiseCredentialsList
          credentials={state.Credentials}
          loading={state.LoadingCredentials}
          hasMoreData={state.HasMoreCredentials}
          canCancelLoad={state.CanCancelCredentials}
          onLoadMore={loadAll => refreshCredentials(false, loadAll)}
          onLoadCancel={() => cancelTokenCredentials.Cancel = true}
          onAction={credentialAction}/>
      </FlexColumn>
      <FlexColumn style={{marginTop: '1em'}}>
        <h3>Configurations</h3>
        <FlexRowCentered style={{padding: '0.5em 0', flex: 0}}>
          <BlueButton onClick={startNewConfiguration}>
            <FlexRowCentered>
              <PlusIcon style={{marginRight: '0.5em'}}/>
              <span style={Theme.typography.labelMedium}>New Configuration</span>
            </FlexRowCentered>
          </BlueButton>
          <FlexFill/>
          <Tooltip content={'Refresh Configurations'}>
            <IconButton
              onClick={() => refreshConfigs(true, false)}
              renderIcon={() => <ArrowRotateTwoIcon/>}
              style={{marginRight: '1em'}}/>
          </Tooltip>
        </FlexRowCentered>
        <ProjectWiseConfigurationsList
          configurations={state.Configurations}
          loading={state.LoadingConfigurations}
          hasMoreData={state.HasMoreConfigurations}
          canCancelLoad={state.CanCancelConfigurations}
          onLoadMore={loadAll => refreshConfigs(false, loadAll)}
          onLoadCancel={() => cancelTokenConfigurations.Cancel = true}
          onAction={configurationAction}/>
      </FlexColumn>

      <Modal open={state.IsEditingCredential}>
        <Modal.Header>{state.EditIsNew ? 'Create' : 'Edit'} Credential</Modal.Header>
        <Modal.Body>
          <FlexColumn style={{height: '100%'}}>
            <FlexRowCentered>
              <ColumnLeft>
                <Tooltip content={'The display name of the credential'}>
                  Key
                </Tooltip>
              </ColumnLeft>
              <ColumnRight>
                <TextInput value={state.EditingCredential?.key ?? ''}
                           onChange={e => {
                             state.EditingCredential!.key = e.target.value;
                             dispatch({
                               type: ProjectWiseSettingsActions.editingCredential,
                               payload: state.EditingCredential
                             })
                           }}/>
              </ColumnRight>
            </FlexRowCentered>
            <FlexRowCentered>
              <ColumnLeft>
                <Tooltip content={'The username to use to log in'}>
                  Username
                </Tooltip>
              </ColumnLeft>
              <ColumnRight>
                <TextInput value={state.EditingCredential?.username ?? ''}
                           onChange={e => {
                             state.EditingCredential!.username = e.target.value;
                             dispatch({
                               type: ProjectWiseSettingsActions.editingCredential,
                               payload: state.EditingCredential
                             })
                           }}/>
              </ColumnRight>
            </FlexRowCentered>
            <FlexRowCentered>
              <ColumnLeft>
                <Tooltip content={'The password'}>
                  Password
                </Tooltip>
              </ColumnLeft>
              <ColumnRight>
                <FlexFill>
                  <MaskedText allowChange={true}
                              value={state.EditPassword ?? ''}
                              onChange={e => {
                                dispatch({
                                  type: ProjectWiseSettingsActions.editPassword,
                                  payload: e
                                })
                              }}/>
                </FlexFill>
              </ColumnRight>
            </FlexRowCentered>
          </FlexColumn>
        </Modal.Body>
        <Modal.Footer>
          <FlexRow style={{justifyContent: 'end'}}>
            <BlueButton onClick={() => finishEditCredential()}
                        disabled={state.EditingCredential?.key == null
                          || state.EditingCredential?.key.trim() === ''
                          || state.EditingCredential?.username == null
                          || state.EditingCredential?.key.trim() === ''
                          || state.EditPassword == null
                          || state.EditPassword.trim() === ''}>
              <FlexRowCentered>
                <span style={Theme.typography.labelMedium}>Save</span>
              </FlexRowCentered>
            </BlueButton>
            <BlueButton style={{marginLeft: '1em'}}
                        onClick={() => dispatch({
                          type: ProjectWiseSettingsActions.isEditingCredential,
                          payload: false
                        })}>
              <FlexRowCentered>
                <span style={Theme.typography.labelMedium}>Cancel</span>
              </FlexRowCentered>
            </BlueButton>
          </FlexRow>
        </Modal.Footer>
      </Modal>

      <Modal open={state.IsEditingConfiguration}>
        <Modal.Header>{state.EditIsNew ? 'Create' : 'Edit'} Configuration</Modal.Header>
        <Modal.Body>
          <FlexColumn style={{height: '100%'}}>
            <FlexRowCentered>
              <ColumnLeft>
                <Tooltip content={'The ProjectWise server address'}>
                  Server Address
                </Tooltip>
              </ColumnLeft>
              <ColumnRight>
                <TextInput value={state.EditingConfiguration?.ApiConfiguration.serverAddress ?? ''}
                           onChange={e => {
                             state.EditingConfiguration!.ApiConfiguration.serverAddress = e.target.value;
                             dispatch({
                               type: ProjectWiseSettingsActions.editingConfiguration,
                               payload: state.EditingConfiguration
                             })
                           }}/>
              </ColumnRight>
            </FlexRowCentered>
            <FlexRowCentered>
              <ColumnLeft>
                <Tooltip content={'The ID of the ProjectWise repository to use'}>
                  Repository ID
                </Tooltip>
              </ColumnLeft>
              <ColumnRight>
                <TextInput value={state.EditingConfiguration?.ApiConfiguration.repositoryId ?? ''}
                           onChange={e => {
                             state.EditingConfiguration!.ApiConfiguration.repositoryId = e.target.value;
                             dispatch({
                               type: ProjectWiseSettingsActions.editingConfiguration,
                               payload: state.EditingConfiguration
                             })
                           }}/>
              </ColumnRight>
            </FlexRowCentered>
            <FlexRowCentered>
              <ColumnLeft>
                <Tooltip content={'The Name of the ProjectWise repository to use'}>
                  Repository Name
                </Tooltip>
              </ColumnLeft>
              <ColumnRight>
                <FlexFill>
                  <TextInput value={state.EditingConfiguration?.ApiConfiguration.repositoryName ?? ''}
                             onChange={e => {
                               state.EditingConfiguration!.ApiConfiguration.repositoryName = e.target.value;
                               dispatch({
                                 type: ProjectWiseSettingsActions.editingConfiguration,
                                 payload: state.EditingConfiguration
                               })
                             }}/>
                </FlexFill>
              </ColumnRight>
            </FlexRowCentered>
            <FlexRowCentered>
              <ColumnLeft>
                <Tooltip content={'The credential to use to log into the ProjectWise server'}>
                  Credential
                </Tooltip>
              </ColumnLeft>
              <ColumnRight>
                <Selector
                  items={state.CredentialSelectOptions}
                  selected={state.SelectedCredential}
                  onSelectionChange={c => {
                    dispatch({type: ProjectWiseSettingsActions.selectedCredential, payload: c});
                  }}/>
              </ColumnRight>
            </FlexRowCentered>
          </FlexColumn>
        </Modal.Body>
        <Modal.Footer>
          <FlexRow style={{justifyContent: 'end'}}>
            <BlueButton onClick={() => finishEditConfiguration()}
                        disabled={state.EditingConfiguration?.ApiConfiguration.serverAddress == null
                          || state.EditingConfiguration?.ApiConfiguration.serverAddress.trim() === ''
                          || state.EditingConfiguration?.ApiConfiguration.repositoryId == null
                          || state.EditingConfiguration?.ApiConfiguration.repositoryId.trim() === ''
                          || state.EditingConfiguration?.ApiConfiguration.repositoryName == null
                          || state.EditingConfiguration?.ApiConfiguration.repositoryName.trim() === ''
                          || state.SelectedCredential == null}>
              <FlexRowCentered>
                <span style={Theme.typography.labelMedium}>Save</span>
              </FlexRowCentered>
            </BlueButton>
            <BlueButton style={{marginLeft: '1em'}}
                        onClick={() => dispatch({
                          type: ProjectWiseSettingsActions.isEditingConfiguration,
                          payload: false
                        })}>
              <FlexRowCentered>
                <span style={Theme.typography.labelMedium}>Cancel</span>
              </FlexRowCentered>
            </BlueButton>
          </FlexRow>
        </Modal.Footer>
      </Modal>
    </FlexColumn>
  );
};

export default ProjectWiseSettings;